/* ==========================================================================
   Ozeret — putting it on the home screen

   Oster's report: "On my iPhone I didn't actually see the option of adding it
   to the homescreen to get the feel of an app."

   He would not have. The manifest and the touch icon were both already there,
   which is everything a browser needs — and on iOS none of it produces a
   prompt. Safari has never fired the install event; the only route is Share →
   Add to Home Screen, in a menu the app cannot open, mention, or point at.
   Android Chrome does fire the event, but only when it feels like it, and if
   nothing calls preventDefault its own banner may never appear either.

   So the app asks, in the one way each platform allows:

     iOS Safari      the steps, with the share glyph drawn, since we can only
                     describe the menu
     Android Chrome  a real button, from the event we captured
     anything else   nothing at all — a banner telling a desktop browser to
                     install a housekeeping app is noise

   Shown once things are worth keeping — not on the sign-in screen — and never
   again once it has been dismissed or the app is already installed.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { Icon } from './Icon';

/** Chrome's install event. Not in the DOM types because it is not standard. */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED = 'ozeret.install.dismissed';

/** Already launched from the home screen. Two checks because iOS answers the
 *  non-standard one and everything else answers the standard one. */
function installed(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** iOS Safari, where there is no event and no button — only instructions.
 *
 *  Chrome and Firefox on iOS are Safari underneath and carry the same
 *  limitation, but their share menus have no "Add to Home Screen", so telling
 *  somebody in Chrome-on-iPhone to look for it would send them hunting for
 *  something that is not there. Only Safari gets the steps. */
function iosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua)
    /* iPadOS reports itself as a Mac; a touch-capable "Mac" is an iPad. */
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!ios) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function InstallPrompt() {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === '1';
    } catch {
      /* A browser with storage blocked shows the prompt every time, which is
         the right way round to fail: annoying beats never offering it. */
      return false;
    }
  });

  useEffect(() => {
    const grab = (e: Event) => {
      /* Without this Chrome shows its own bar at a moment of its choosing,
         which on this app lands over the tab bar. */
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', grab);
    /* Installed from somewhere else while the page was open. */
    const done = () => setDismissed(true);
    window.addEventListener('appinstalled', done);
    return () => {
      window.removeEventListener('beforeinstallprompt', grab);
      window.removeEventListener('appinstalled', done);
    };
  }, []);

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, '1');
    } catch { /* nothing to do about it */ }
  };

  if (dismissed || installed()) return null;

  const ios = iosSafari();
  if (!event && !ios) return null;

  return (
    <div className="installbar">
      <div className="col grow" style={{ gap: 3 }}>
        <b style={{ fontSize: 14 }}>{t('i_keep')}</b>
        {ios ? (
          <span className="hint">
            Tap <Icon name="share-ios" /> at the bottom of Safari, then{' '}
            <b>{t('i_add')}</b>. It opens like an app, without the address bar.
          </span>
        ) : (
          <span className="hint">
            {t('i_opens')}
          </span>
        )}
      </div>

      {!ios && event && (
        <button
          type="button"
          className="btn sm"
          onClick={async () => {
            await event.prompt();
            /* Whatever they chose, do not ask again this session: the browser
               will not honour a second prompt from the same event anyway. */
            await event.userChoice.catch(() => undefined);
            close();
          }}
        >
          Add it
        </button>
      )}

      <button
        type="button"
        className="iconbtn"
        aria-label={t('i_notnow')}
        onClick={close}
      >
        ✕
      </button>
    </div>
  );
}
